import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import { generatePrintableFlashcards, generateFlashcardTest } from '../utils/pdfGenerator';

const FlashcardViewer = ({ setId, title, onBack }) => {
  const [allCards, setAllCards] = useState([]);
  const [cards, setCards] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [studyMode, setStudyMode] = useState(null); // null = not started, 'all', 'missed'
  const [wrongCardIds, setWrongCardIds] = useState([]);
  const [correctCount, setCorrectCount] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [sessionPoints, setSessionPoints] = useState(0);
  const [cardsStudied, setCardsStudied] = useState(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false); // Prevent double submissions
  const [sessionComplete, setSessionComplete] = useState(false); // Prevent multiple completions
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchFlashcards();
  }, [setId]);

  // Refetch wrong cards when returning to study mode selection
  useEffect(() => {
    if (studyMode === null && allCards.length > 0) {
      fetchFlashcards();
    }
  }, [studyMode]);

  const fetchFlashcards = async () => {
    try {
      const response = await api.get(`/flashcards/sets/${setId}`);
      setAllCards(response.data.cards);
      
      // Fetch wrong cards
      const wrongResponse = await api.get(`/flashcards/sets/${setId}/wrong-cards`);
      setWrongCardIds(wrongResponse.data.wrong_card_ids || []);
      
      // Fetch correct cards count for mastery calculation
      const correctResponse = await api.get(`/flashcards/sets/${setId}/correct-cards`);
      setCorrectCount(correctResponse.data.correct_count || 0);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to load flashcards",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const startStudyMode = (mode) => {
    setStudyMode(mode);
    
    if (mode === 'all') {
      setCards(allCards);
    } else if (mode === 'missed') {
      const missedCards = allCards.filter(card => wrongCardIds.includes(card.card_id));
      if (missedCards.length === 0) {
        toast({
          title: "No Missed Cards",
          description: "You haven't marked any cards as wrong yet. Start with 'Study All'!",
          variant: "default"
        });
        return;
      }
      setCards(missedCards);
    }
    
    setCurrentIndex(0);
    setShowAnswer(false);
    setIsFlipped(false);
    setSessionPoints(0);
    setCardsStudied(new Set());
    setIsSubmitting(false);
    setSessionComplete(false);
  };

  const handleMarkWrong = async () => {
    // Prevent double submissions
    if (isSubmitting || sessionComplete) return;
    
    const currentCard = cards[currentIndex];
    const isLastCard = currentIndex === cards.length - 1;
    
    // Mark that this card was studied (no points awarded)
    setCardsStudied(prev => new Set([...prev, currentCard.card_id]));
    
    // If last card, set submitting immediately to prevent multiple clicks
    if (isLastCard) {
      setIsSubmitting(true);
      setSessionComplete(true);
    }
    
    try {
      await api.post(`/flashcards/sets/${setId}/mark-wrong/${currentCard.card_id}`);
      
      // Add to wrong cards list if not already there
      if (!wrongCardIds.includes(currentCard.card_id)) {
        setWrongCardIds([...wrongCardIds, currentCard.card_id]);
      }
      
      if (!isLastCard) {
        toast({
          title: "Marked as Wrong",
          description: "This card has been saved to your missed cards",
          variant: "default"
        });
      }
      
      // If last card, award points for session and return
      if (isLastCard) {
        try {
          if (sessionPoints > 0) {
            const response = await api.post(`/flashcards/sets/${setId}/award-points?points_earned=${sessionPoints}`);
            
            toast({
              title: "Study Complete!",
              description: `You earned ${response.data.points_earned} points! New total: ${response.data.new_total}`,
              variant: "default"
            });
          } else {
            toast({
              title: "Study Complete!",
              description: "Keep practicing to earn more points!",
              variant: "default"
            });
          }
        } catch (error) {
          console.error('Error awarding points:', error);
        }
        
        setTimeout(() => {
          onBack(); // Return to flashcard sets list
        }, 2000);
      } else {
        // Move to next card
        handleNext();
      }
    } catch (error) {
      // Reset submitting state on error if it was the last card
      if (isLastCard) {
        setIsSubmitting(false);
        setSessionComplete(false);
      }
      toast({
        title: "Error",
        description: "Failed to mark card as wrong",
        variant: "destructive"
      });
    }
  };

  const handleMarkRight = async () => {
    // Prevent double submissions
    if (isSubmitting || sessionComplete) return;
    
    const currentCard = cards[currentIndex];
    const isLastCard = currentIndex === cards.length - 1;
    const cardPoints = currentCard.points || 10;
    
    // Calculate points to award (only if card hasn't been studied yet)
    const pointsToAdd = !cardsStudied.has(currentCard.card_id) ? cardPoints : 0;
    
    // Add points if this card hasn't been studied yet in this session
    if (!cardsStudied.has(currentCard.card_id)) {
      setSessionPoints(prev => prev + cardPoints);
      setCardsStudied(prev => new Set([...prev, currentCard.card_id]));
    }
    
    // If last card, set submitting immediately to prevent multiple clicks
    if (isLastCard) {
      setIsSubmitting(true);
      setSessionComplete(true);
    }
    
    // Mark card as correct
    try {
      await api.post(`/flashcards/sets/${setId}/mark-correct/${currentCard.card_id}`);
      
      // Remove from wrong cards list if present
      if (wrongCardIds.includes(currentCard.card_id)) {
        setWrongCardIds(wrongCardIds.filter(id => id !== currentCard.card_id));
      }
      
      if (!isLastCard) {
        toast({
          title: "Correct! +" + cardPoints + " points",
          description: wrongCardIds.includes(currentCard.card_id) 
            ? "This card has been removed from your missed cards" 
            : "Great job!",
          variant: "default"
        });
      }
    } catch (error) {
      console.error('Error marking card as correct:', error);
    }
    
    // If last card, award points and return
    if (isLastCard) {
      try {
        // Use the current sessionPoints + pointsToAdd for the final award
        const finalPoints = sessionPoints + pointsToAdd;
        const response = await api.post(`/flashcards/sets/${setId}/award-points?points_earned=${finalPoints}`);
        
        toast({
          title: "🎉 Study Complete!",
          description: `You earned ${response.data.points_earned} points! New total: ${response.data.new_total}`,
          variant: "default"
        });
      } catch (error) {
        console.error('Error awarding points:', error);
      }
      
      setTimeout(() => {
        onBack(); // Return to flashcard sets list
      }, 2000);
    } else {
      // Move to next card
      handleNext();
    }
  };

  const handleShuffle = () => {
    const shuffled = [...cards].sort(() => Math.random() - 0.5);
    setCards(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
    toast({
      title: "Cards Shuffled!",
      description: "The flashcards have been randomized"
    });
  };

  const handleNext = () => {
    if (currentIndex < cards.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowAnswer(false);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setShowAnswer(false);
      setIsFlipped(false);
    }
  };

  const handleFlip = () => {
    setIsFlipped(!isFlipped);
  };

  const handlePrintCards = async () => {
    try {
      await generatePrintableFlashcards(cards, title);
      toast({
        title: "PDF Generated!",
        description: "Your printable flashcards are ready to download"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive"
      });
    }
  };

  const handlePrintTest = async () => {
    try {
      await generateFlashcardTest(cards, title);
      toast({
        title: "Test Generated!",
        description: "Your practice test is ready to download"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to generate test",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading flashcards...</p>
        </div>
      </div>
    );
  }

  if (allCards.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 flex items-center justify-center p-6">
        <Card className="max-w-md w-full">
          <CardContent className="py-12 text-center">
            <p className="text-gray-600 text-lg mb-4">This flashcard set is empty.</p>
            <Button onClick={onBack}>← Back to Sets</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Study Mode Selection Screen
  if (!studyMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 p-6">
        <div className="max-w-2xl mx-auto">
          <div className="mb-6">
            <Button onClick={onBack} variant="outline">
              ← Back to Sets
            </Button>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle className="text-3xl">{title}</CardTitle>
              <div className="flex items-center justify-between mt-2">
                <p className="text-gray-600">{allCards.length} cards in this set</p>
                {allCards.length > 0 && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Your Mastery:</span>
                    <span className={`text-lg font-bold ${
                      (correctCount / allCards.length * 100) >= 80 ? 'text-green-600' :
                      (correctCount / allCards.length * 100) >= 50 ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {Math.round(correctCount / allCards.length * 100)}%
                    </span>
                    <span className="text-xs text-gray-500">({correctCount}/{allCards.length})</span>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">Choose Your Study Mode</h2>
                <p className="text-gray-600">How would you like to study today?</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Study All */}
                <div 
                  className="border-2 border-purple-200 rounded-lg p-6 hover:border-purple-400 hover:shadow-lg transition-all cursor-pointer bg-white"
                  onClick={() => startStudyMode('all')}
                >
                  <div className="text-center">
                    <div className="text-5xl mb-4">📚</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Study All</h3>
                    <p className="text-gray-600 mb-4">
                      Practice all {allCards.length} cards in this set
                    </p>
                    <Button className="w-full">Start</Button>
                  </div>
                </div>

                {/* Study Missed */}
                <div 
                  className={`border-2 rounded-lg p-6 transition-all ${
                    wrongCardIds.length > 0 
                      ? 'border-red-200 hover:border-red-400 hover:shadow-lg cursor-pointer bg-white' 
                      : 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  }`}
                  onClick={() => wrongCardIds.length > 0 && startStudyMode('missed')}
                >
                  <div className="text-center">
                    <div className="text-5xl mb-4">🎯</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">Study Missed</h3>
                    <p className="text-gray-600 mb-4">
                      {wrongCardIds.length > 0 
                        ? `Practice ${wrongCardIds.length} card${wrongCardIds.length === 1 ? '' : 's'} you got wrong`
                        : 'No missed cards yet'}
                    </p>
                    <Button 
                      className="w-full" 
                      disabled={wrongCardIds.length === 0}
                      variant={wrongCardIds.length > 0 ? 'default' : 'outline'}
                    >
                      {wrongCardIds.length > 0 ? 'Start' : 'No Cards Yet'}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-semibold text-gray-800 mb-2">💡 Self-Test Mode</h4>
                <p className="text-sm text-gray-600">
                  Study with self-assessment! See the question, try to answer it, then check your response and mark yourself as right or wrong. Cards you mark as wrong will be saved so you can focus on them later.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 via-blue-50 to-cyan-100 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">{title}</h1>
            <div className="flex items-center gap-3 mt-2">
              <p className="text-gray-600">
                Card {currentIndex + 1} of {cards.length}
              </p>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                studyMode === 'missed' 
                  ? 'bg-red-100 text-red-700' 
                  : 'bg-purple-100 text-purple-700'
              }`}>
                {studyMode === 'missed' ? '🎯 Study Missed' : '📚 Study All'}
              </span>
              <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-700">
                ⭐ {sessionPoints} pts
              </span>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={handlePrintCards} variant="outline" size="sm">
              🖨️ Print Cards
            </Button>
            <Button onClick={handlePrintTest} variant="outline" size="sm">
              📝 Print Test
            </Button>
            <Button onClick={handleShuffle} variant="outline">
              🔀 Shuffle
            </Button>
            <Button onClick={() => setStudyMode(null)} variant="outline">
              ← Change Mode
            </Button>
          </div>
        </div>

        {/* Flashcard */}
        <div className="mb-6">
          <Card className="min-h-[400px] transition-all duration-300 shadow-xl">
            <CardContent className="flex flex-col items-center justify-center min-h-[400px] p-8">
              {/* Question - Always Visible */}
              <div className="text-center mb-6 w-full">
                <p className="text-sm text-gray-500 mb-4 uppercase tracking-wide font-semibold">Question</p>
                <div 
                  className="text-2xl font-semibold text-gray-800 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: currentCard.question }}
                />
              </div>

              {/* Answer - Show on button click */}
              {showAnswer && (
                <div className="text-center w-full border-t-2 border-gray-200 pt-6 animate-fadeIn">
                  <p className="text-sm text-purple-500 mb-4 uppercase tracking-wide font-semibold">Answer</p>
                  <div 
                    className="text-2xl font-semibold text-purple-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: currentCard.answer }}
                  />
                </div>
              )}

              {/* Show Answer Button */}
              {!showAnswer && (
                <Button 
                  onClick={() => setShowAnswer(true)}
                  variant="outline"
                  className="mt-4"
                >
                  Show Answer
                </Button>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Self-Assessment Buttons */}
        {showAnswer && (
          <div className="mb-6">
            <p className="text-center text-gray-600 mb-4 font-semibold">
              {sessionComplete ? "Finishing..." : "Did you get it right?"}
            </p>
            <div className="flex items-center justify-center gap-6">
              <Button 
                onClick={handleMarkWrong}
                size="lg"
                variant="destructive"
                className="min-w-[150px] text-lg"
                disabled={isSubmitting || sessionComplete}
              >
                {isSubmitting ? "..." : "❌ Wrong"}
              </Button>
              
              <Button 
                onClick={handleMarkRight}
                size="lg"
                className="min-w-[150px] text-lg bg-green-600 hover:bg-green-700"
                disabled={isSubmitting || sessionComplete}
              >
                {isSubmitting ? "..." : "✅ Right"}
              </Button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-center gap-8 mb-8">
          <Button 
            onClick={handlePrevious} 
            disabled={currentIndex === 0}
            variant="outline"
            size="lg"
            className="min-w-[120px]"
          >
            ← Previous
          </Button>
          
          <div className="text-center px-4">
            <p className="text-2xl font-bold text-purple-700">
              {currentIndex + 1}/{cards.length}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {studyMode === 'missed' ? 'Missed Cards' : 'All Cards'}
            </p>
          </div>

          <Button 
            onClick={handleNext} 
            disabled={currentIndex === cards.length - 1}
            variant="outline"
            size="lg"
            className="min-w-[120px]"
          >
            Next →
          </Button>
        </div>

        {/* Instructions */}
        <div className="text-center pb-20">
          <p className="text-sm text-gray-600">
            Cards marked as wrong will be saved for future study sessions
          </p>
        </div>
      </div>
    </div>
  );
};

export default FlashcardViewer;
